import __decorate from "../_virtual/_@oxc-project_runtime@0.147.0/helpers/esm/decorate.js";
import { LitElement, css, html, nothing } from "lit";
import { property, state } from "lit/decorators.js";
import "@progressive-development/pd-icon/pd-icon";
import { msg } from "@lit/localize";
import { classMap } from "lit/directives/class-map.js";
//#region src/pd-panel-viewer/PdPanelViewer.ts
/**
* @tagname pd-panel-viewer
*
* @slot - Default slot for pd-panel children.
*
* @event exited - Dispatched on a child panel element when the viewer navigates away from it.
* @event entered - Dispatched on a child panel element when the viewer navigates to it.
*
* @cssprop --pd-panel-viewer-bg-col - Viewer background color. Default: `var(--pd-default-col)`.
* @cssprop --pd-panel-width - Max width of the panel viewer. Default: `1170px`.
* @attr {boolean} fill - Fill parent height instead of using --pd-panel-height.
* @cssprop --pd-panel-height - Height of the panel viewer. Default: `60vh`.
* @cssprop --pd-panel-overflow - Overflow behavior. Default: `hidden`.
* @cssprop --pd-panel-nav-top - Top position of navigation arrows. Default: `calc(50% - 25px)`.
* @cssprop --pd-panel-nav-bg-col - Circle behind the arrows in `overlayNav` mode. Default: `rgba(0, 0, 0, 0.15)`; `transparent` for bare arrows.
* @cssprop --pd-panel-nav-bg-col-hover - Circle colour on hover in `overlayNav` mode. Default: `rgba(0, 0, 0, 0.3)`.
* @cssprop --pd-panel-nav-backdrop - `backdrop-filter` of the circle in `overlayNav` mode. Default: `blur(4px)`; `none` together with a transparent circle.
* @cssprop --pd-panel-icon-col - Navigation icon color. Default: `var(--pd-on-primary-col)`.
* @cssprop --pd-panel-icon-col-hover - Navigation icon hover color. Default: `var(--pd-on-primary-col)`.
* @cssprop --pd-panel-icon-col-disabled - Navigation icon disabled color. Default: `var(--pd-default-disabled-col)`.
* @cssprop --pd-panel-progress-col - Progress bar background color. Default: `rgba(255, 255, 255, 0.4)`.
* @cssprop --pd-panel-progress-watched-col - Progress bar watched segment color. Default: `var(--pd-on-primary-col)`.
* @cssprop --pd-panel-progress-height - Progress bar segment height. Default: `4px`.
* @cssprop --pd-panel-progress-border-radius - Progress bar segment border radius. Default: `0`.
* @cssprop --pd-panel-progress-margin - Progress bar container margin. Default: `0.5rem auto`.
*/
var PdPanelViewer = class extends LitElement {
	constructor(..._args) {
		super(..._args);
		this.withProgress = false;
		this.overlayNav = true;
		this.deltaCalc = 3;
		this._index = 0;
		this._panData = {};
		this._update = {};
		this._resizeObserver = null;
		this._pointerStartX = 0;
		this._pointerStartY = 0;
		this._activePointerId = null;
		this._pointerMoved = false;
		this._dragThreshold = 5;
		this._isHorizontalDrag = null;
		this._onPointerDown = (e) => {
			if (e.button !== 0) return;
			this._activePointerId = e.pointerId;
			this._pointerStartX = e.clientX;
			this._pointerStartY = e.clientY;
			this._pointerMoved = false;
			this._isHorizontalDrag = null;
		};
		this._onPointerMove = (e) => {
			if (e.pointerId !== this._activePointerId) return;
			const deltaX = e.clientX - this._pointerStartX;
			const deltaY = e.clientY - this._pointerStartY;
			if (this._isHorizontalDrag === null) {
				const absDx = Math.abs(deltaX);
				const absDy = Math.abs(deltaY);
				if (absDx < this._dragThreshold && absDy < this._dragThreshold) return;
				this._isHorizontalDrag = absDx > absDy;
				if (this._isHorizontalDrag) {
					e.currentTarget.setPointerCapture(e.pointerId);
					this.style.userSelect = "none";
					window.getSelection()?.removeAllRanges();
				}
				if (!this._isHorizontalDrag) {
					this._activePointerId = null;
					return;
				}
			}
			if (!this._isHorizontalDrag) return;
			e.preventDefault();
			this._pointerMoved = true;
			this._panData = {
				deltaX,
				isFinal: false
			};
		};
		this._onPointerUp = (e) => {
			if (e.pointerId !== this._activePointerId) return;
			if (this._pointerMoved && this._isHorizontalDrag) {
				const deltaX = e.clientX - this._pointerStartX;
				this._panData = {
					deltaX,
					isFinal: true
				};
			}
			this._activePointerId = null;
			this._isHorizontalDrag = null;
			this.style.userSelect = "";
		};
		this._onPointerCancel = (e) => {
			if (e.pointerId !== this._activePointerId) return;
			this._panData = {
				deltaX: 0,
				isFinal: true
			};
			this._activePointerId = null;
			this._isHorizontalDrag = null;
			this.style.userSelect = "";
		};
	}
	static {
		this.styles = css`
    :host {
      display: flex;
      flex-direction: column;
      width: 100%;
      height: 100%;
      max-width: var(--pd-panel-width, 1170px);
      overflow: var(--pd-panel-overflow, hidden);
      background-color: var(--pd-panel-viewer-bg-col, var(--pd-default-col));
    }

    .panel-container {
      position: relative;
      height: var(--pd-panel-height, 60vh);
      width: 100%;
      display: flex;
      flex-direction: column;
      padding: 0 2rem;
      box-sizing: border-box;
    }

    .panel-content {
      position: relative;
      width: 100%;
      height: 100%;
      touch-action: pan-y;
    }

    pd-icon {
      position: absolute;
      top: var(--pd-panel-nav-top, calc(50% - 25px));
      height: 50px;
      cursor: pointer;
      z-index: 1;
      --pd-icon-col-active: var(--pd-panel-icon-col, var(--pd-on-primary-col));
      --pd-icon-col-hover: var(
        --pd-panel-icon-col-hover,
        var(--pd-on-primary-col)
      );
      --pd-icon-col-disabled: var(
        --pd-panel-icon-col-disabled,
        var(--pd-default-disabled-col)
      );
    }

    #prev {
      left: 0;
    }

    #next {
      right: 0;
    }

    :host([fill]) .panel-container {
      height: 100%;
    }

    :host([overlayNav]) .panel-container {
      padding: 0;
    }

    :host([overlayNav]) pd-icon {
      margin: 0 0.25rem;
      --pd-icon-bg-col: var(--pd-panel-nav-bg-col, rgba(0, 0, 0, 0.15));
      --pd-icon-bg-col-hover: var(
        --pd-panel-nav-bg-col-hover,
        rgba(0, 0, 0, 0.3)
      );
      backdrop-filter: var(--pd-panel-nav-backdrop, blur(4px));
    }

    @media (max-width: 768px) {
      .panel-container {
        padding: 0 0.5rem;
      }
      pd-icon {
        height: 36px;
        top: var(--pd-panel-nav-top, calc(50% - 18px));
      }
    }

    #progress {
      position: relative;
      height: 20px;
      width: 50%;
      margin: var(--pd-panel-progress-margin, 0.5rem auto);
      display: grid;
      grid-auto-flow: column;
      grid-auto-columns: 1fr;
      grid-gap: 10px;
      align-content: center;
      align-self: flex-end;
    }

    #progress > div {
      background: var(--pd-panel-progress-col, rgba(255, 255, 255, 0.4));
      height: var(--pd-panel-progress-height, 4px);
      border-radius: var(--pd-panel-progress-border-radius, 0);
      transition: background 0.3s linear;
      cursor: pointer;
    }

    #progress > div.watched {
      background: var(
        --pd-panel-progress-watched-col,
        var(--pd-on-primary-col)
      );
    }

    ::slotted(*) {
      position: absolute;
      width: 100%;
      height: calc(100%);
      transition: transform 0.35s ease-out;
      left: 0;
    }
  `;
	}
	get index() {
		return this._index;
	}
	set index(value) {
		const prev = this._index;
		this._index = value;
		this.children[prev].dispatchEvent(new CustomEvent("exited", { bubbles: true }));
		this.children[value].dispatchEvent(new CustomEvent("entered", { bubbles: true }));
	}
	render() {
		return html`
      <div
        class="panel-container"
        role="region"
        aria-roledescription="carousel"
        aria-label="${msg("Panel viewer", { id: "pd.panel.viewer.aria.label" })}"
        aria-live="polite"
      >
        <div
          class="panel-content"
          @pointerdown=${this._onPointerDown}
          @pointermove=${this._onPointerMove}
          @pointerup=${this._onPointerUp}
          @pointercancel=${this._onPointerCancel}
          @dragstart=${(e) => e.preventDefault()}
        >
          <slot></slot>
        </div>
        ${this.children.length > 1 ? html`
                <pd-icon
                  id="prev"
                  class=${classMap({ round: this.overlayNav })}
                  icon="previousArrow"
                  ?activeIcon="${true}"
                  ?disabled="${this.index <= 0}"
                  aria-label="${msg("Previous panel", { id: "pd.panel.viewer.prev" })}"
                  role="button"
                  tabindex="0"
                  @click=${this.previous}
                  @keydown=${this._onNavKeyDown}
                ></pd-icon>
                <pd-icon
                  id="next"
                  class=${classMap({ round: this.overlayNav })}
                  icon="nextArrow"
                  ?activeIcon="${true}"
                  ?disabled="${this.index === this.children.length - 1}"
                  aria-label="${msg("Next panel", { id: "pd.panel.viewer.next" })}"
                  role="button"
                  tabindex="0"
                  @click=${this.next}
                  @keydown=${this._onNavKeyDown}
                ></pd-icon>
              ` : nothing}
      </div>
      ${this.withProgress ? html` <div
              id="progress"
              role="tablist"
              aria-label="${msg("Panel navigation", { id: "pd.panel.viewer.progress" })}"
            >
              ${Array.from(this.children).map((childEl, i) => html` <div
                    role="tab"
                    tabindex="0"
                    aria-selected="${i === this.index}"
                    aria-label="${msg("Panel", { id: "pd.panel.viewer.panel" })} ${i + 1}"
                    @click="${() => {
			this.index = i;
		}}"
                    @keydown="${(e) => this._onProgressKeyDown(e, i)}"
                    class=${classMap({ watched: i <= this.index })}
                  ></div>`)}
            </div>` : nothing}
    `;
	}
	firstUpdated() {
		this._resizeObserver = new ResizeObserver(() => {
			this._panData = {};
			this._updateLayout();
		});
		this._resizeObserver.observe(this);
	}
	disconnectedCallback() {
		super.disconnectedCallback();
		if (this._resizeObserver) {
			this._resizeObserver.disconnect();
			this._resizeObserver = null;
		}
	}
	update(changedProperties) {
		super.update(changedProperties);
		this._updateLayout();
	}
	/**
	* Positions all panels using translate3d + scale based on current index and pan delta.
	* Handles snap-to-panel decision when pan gesture ends (isFinal).
	*
	* Historical note: Layout-Berechnung aus dem original StackBlitz-Beispiel übernommen.
	* @see https://stackblitz.com/edit/lit-story-viewer?file=story-viewer.ts
	*/
	_updateLayout() {
		const { isFinal = false } = this._panData;
		let { deltaX = 0 } = this._panData;
		const width = this.clientWidth;
		if (width === 0) return;
		const minScale = .8;
		if (isFinal && deltaX !== 0) {
			if (deltaX > width / this.deltaCalc) this.previous();
			else if (deltaX < -width / this.deltaCalc) this.next();
			this._panData = {};
			deltaX = 0;
		}
		deltaX = isFinal ? 0 : deltaX;
		Array.from(this.children).forEach((el, i) => {
			const x = (i - this.index) * width + deltaX;
			const u = deltaX / width + (i - this.index);
			const v = -Math.abs(u * .19999999999999996) + 1;
			const scale = Math.max(v, minScale);
			const htmlEl = el;
			htmlEl.style.transform = `translate3d(${x}px,0,0) scale(${scale})`;
			htmlEl.style.opacity = String(scale);
		});
	}
	next() {
		this.index = Math.max(0, Math.min(this.children.length - 1, this.index + 1));
		this._update = false;
	}
	previous() {
		this.index = Math.max(0, Math.min(this.children.length - 1, this.index - 1));
		this._update = false;
	}
	_onNavKeyDown(e) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			e.currentTarget?.click();
		}
	}
	_onProgressKeyDown(e, index) {
		if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			this.index = index;
		} else if (e.key === "ArrowRight" || e.key === "ArrowDown") {
			e.preventDefault();
			this.next();
		} else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
			e.preventDefault();
			this.previous();
		}
	}
};
__decorate([property({ type: Boolean })], PdPanelViewer.prototype, "withProgress", void 0);
__decorate([property({
	type: Boolean,
	reflect: true
})], PdPanelViewer.prototype, "overlayNav", void 0);
__decorate([property({ type: Number })], PdPanelViewer.prototype, "deltaCalc", void 0);
__decorate([state()], PdPanelViewer.prototype, "_index", void 0);
__decorate([state()], PdPanelViewer.prototype, "_panData", void 0);
__decorate([state()], PdPanelViewer.prototype, "_update", void 0);
//#endregion
export { PdPanelViewer };
